import { useState, useEffect } from 'react';
import { api } from '../api';
import { useConfirm } from './ConfirmDialog';
import ImageManager from './ImageManager';
import { usePremium } from '../contexts/PremiumContext';
import PaywallModal from './PaywallModal';
import { useTranslation } from 'react-i18next';

export default function ItemDetailModal({ item, listType, locations, storageUnits = [], onClose, onSaved, onDelete }) {
  const [tab, setTab] = useState('info'); // info | images | move
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    quantity: item.quantity,
    location_id: item.location_id || '',
    expiry_date: item.expiry_date || '',
    notes: item.notes || '',
    opened: item.opened || 0,
    opened_at: item.opened_at || '',
  });
  const [images, setImages] = useState(item.images || []);
  const [saving, setSaving] = useState(false);
  const { confirm, ConfirmDialog } = useConfirm();

  // Move state
  const [moveQty, setMoveQty] = useState(1);
  const [moveTarget, setMoveTarget] = useState(listType === 'freezer' ? 'fridge' : 'freezer');
  const [moveTargetUnit, setMoveTargetUnit] = useState(null);
  const [moveTargetLocation, setMoveTargetLocation] = useState(null);
  const [moving, setMoving] = useState(false);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const { isPremium } = usePremium();
  const [showPaywall, setShowPaywall] = useState(false);
  const [allLocations, setAllLocations] = useState([]);

  // Hent ALLE lokationer på tværs af liste-typer til dropdown
  useEffect(() => {
    api.getLocations().then(setAllLocations).catch(() => {});
  }, []);

  const save = async () => {
    if (saving) return; // Guard mod double-kald
    setSaving(true);
    try {
      await api.updateInventory(item.id, {
        quantity: parseInt(form.quantity),
        location_id: form.location_id || null,
        expiry_date: form.expiry_date || null,
        notes: form.notes || null,
        opened: form.opened ? 1 : 0,
        opened_at: form.opened ? (form.opened_at || new Date().toISOString().slice(0, 10)) : null,
      });
      setEditing(false);
      onSaved();
    } catch (err) {
      if (err.message?.includes('kræver premium') ||
          err.message?.includes('Gratis version') ||
          err.message?.includes('ekstra enhed')) {
        setSaving(false); // Reset FØR paywall vises
        setShowPaywall(true);
        return;
      }
    } finally { setSaving(false); }
  };

  // Hjælper: er en given lokations-id i en låst enhed?
  const isLockedLocation = (locId) => {
    if (!locId || isPremium) return false;
    // Søg i storageUnits efter en enhed der matcher denne lokation
    // Vi bruger unit_name feltet som locations GET returnerer
    // Da locations prop kun er current listType, kan vi ikke altid finde den
    // Backend er den sikre check — vi viser bare paywall EFTER fejl
    return false;
  };

  const useOne = async () => { await api.useInventory(item.id); onSaved(); };



  const moveItems = async () => {
    const qty = Math.min(parseInt(moveQty) || 1, item.quantity);
    if (qty <= 0) return;
    setMoving(true);
    try {
      if (qty >= item.quantity) {
        await api.deleteInventory(item.id);
      } else {
        await api.updateInventory(item.id, { ...form, quantity: item.quantity - qty });
      }
      await api.addInventory({
        product_id: item.product_id,
        quantity: qty,
        list_type: moveTarget,
        location_id: moveTargetLocation || null,
        expiry_date: item.expiry_date || null,
        date_added: item.date_added || null,
        notes: item.notes || null,
      });
      onSaved();
    } catch (err) {
      if (err.code === 'LIMIT_UNIT_LOCKED' || err.code === 'LIMIT_INVENTORY') {
        setShowPaywall(true);
      }
    } finally { setMoving(false); }
  };

  const { t } = useTranslation();
  const LIST_LABELS = { freezer: `🧊 ${t('nav.freezer')}`, fridge: `🥛 ${t('nav.fridge')}`, pantry: `🥫 ${t('nav.pantry')}` };
  const targets = Object.entries(LIST_LABELS).filter(([k]) => k !== listType);

  return (
    <div className="fixed inset-0 bg-black/60 z-40 flex items-end" onClick={onClose}>
      {ConfirmDialog}
      {showPaywall && <PaywallModal onClose={() => setShowPaywall(false)} reason={t('paywall.reasonLocation')} />}
      <div className="w-full max-h-[90vh] bg-slate-800 rounded-t-3xl overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="px-4 pt-3 pb-3 border-b border-slate-700 flex-shrink-0">
          <div className="flex justify-center mb-2">
            <div className="ft-sheet-handle" />
          </div>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-xl font-bold">{item.name}</h2>
              {item.brand && <p className="text-sm text-slate-400">{item.brand}</p>}
            </div>
            <button onClick={onClose} className="text-slate-400 text-3xl leading-none w-10 h-10 flex items-center justify-center">×</button>
          </div>
          <div className="flex gap-1 mt-3 overflow-x-auto">
            {[['info', t('item.tabInfo')], ['images',`📷 ${t('storage.viewImages')} (${images.length + (item.thumbnail ? 1 : 0)})`], ['move', t('item.tabMove')]].map(([tabKey, l]) => (
              <button key={tabKey} onClick={() => setTab(tabKey)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex-shrink-0
                  ${tab === tabKey ? 'bg-blue-600 text-white' : 'text-slate-400 bg-slate-700'}`}>{l}</button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4"
          style={{ paddingBottom: 'calc(5rem + env(safe-area-inset-bottom))' }}>

          {/* INFO TAB */}
          {tab === 'info' && (
            <div className="space-y-4">
              {!editing && (
                <div className="flex gap-2">
                  <button onClick={useOne} className="flex-1 py-3 bg-blue-600 rounded-xl text-white font-semibold active:bg-blue-700">{t('item.useOne')}</button>
                  <button onClick={() => setEditing(true)} className="flex-1 py-3 bg-slate-700 rounded-xl text-white font-semibold active:bg-slate-600">{t('item.edit')}</button>
                </div>
              )}
              {editing ? (
                <div className="space-y-3">
                  <div className="space-y-1">
                    <label className="text-sm text-slate-400">{t('item.quantity')}</label>
                    <div className="flex items-center gap-3">
                      <button onClick={() => f('quantity', Math.max(0, form.quantity - 1))}
                        className="w-12 h-12 rounded-xl bg-slate-700 text-2xl font-bold active:bg-slate-600">−</button>
                      <input type="number" value={form.quantity} onChange={e => f('quantity', parseInt(e.target.value)||0)}
                        className="flex-1 bg-slate-700 rounded-xl px-4 py-3 text-center text-xl font-bold text-white outline-none border border-slate-600" />
                      <button onClick={() => f('quantity', form.quantity + 1)}
                        className="w-12 h-12 rounded-xl bg-slate-700 text-2xl font-bold active:bg-slate-600">+</button>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm text-slate-400">{t('item.location')}</label>
                    <select value={form.location_id} onChange={e => f('location_id', e.target.value)}
                      className="input-field">
                      <option value="">{t('item.noLocation')}</option>
                      {storageUnits.filter(u => u.list_type === listType).length > 0
                        ? storageUnits.filter(u => u.list_type === listType).map(unit => {
                            const unitLocs = allLocations.filter(l => l.storage_unit_id === unit.id);
                            const locked = unit.position > 0 && !isPremium;
                            return (
                              <optgroup key={unit.id} label={`${locked ? '🔒 ' : ''}${unit.name}`}>
                                {unitLocs.length > 0
                                  ? unitLocs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)
                                  : <option disabled value="">{t('item.noDrawers')}</option>
                                }
                              </optgroup>
                            );
                          })
                        : locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)
                      }
                    </select>
                  </div>
                  {listType !== 'freezer' && (
                    <div className="space-y-1">
                      <label className="text-sm text-slate-400">{t('item.expiryDate')}</label>
                      <input type="date" value={form.expiry_date} onChange={e => f('expiry_date', e.target.value)}
                        className="input-field" />
                    </div>
                  )}
                  <div className="space-y-1">
                    <label className="text-sm text-slate-400">{t('item.openedStatus')}</label>
                    <button type="button" onClick={() => {
                      const nowOpen = !form.opened;
                      f('opened', nowOpen ? 1 : 0);
                      if (nowOpen && !form.opened_at) f('opened_at', new Date().toISOString().slice(0, 10));
                      if (!nowOpen) f('opened_at', '');
                    }}
                      className={`w-full py-3 rounded-xl text-sm font-medium transition-colors
                        ${form.opened ? 'bg-amber-600/30 text-amber-400 border border-amber-600' : 'bg-slate-700 text-slate-400 border border-slate-600'}`}>
                      {form.opened ? t('item.openedStatus') : t('item.notOpenedStatus')}
                    </button>
                    {!!form.opened && (
                      <div className="space-y-1 mt-1">
                        <label className="text-xs text-slate-500">{t('item.openedDate')}</label>
                        <input type="date" value={form.opened_at} onChange={e => f('opened_at', e.target.value)}
                          className="w-full bg-slate-700 rounded-xl px-4 py-2.5 text-white outline-none border border-slate-600 text-sm" />
                      </div>
                    )}
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm text-slate-400">{t('item.notes')}</label>
                    <input value={form.notes} onChange={e => f('notes', e.target.value)}
                      className="input-field" placeholder={t('add.notesPlaceholder')} />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setEditing(false)} className="flex-1 py-3 bg-slate-700 rounded-xl text-white active:bg-slate-600">{t('common.cancel')}</button>
                    <button type="button" onClick={save} disabled={saving} className="flex-1 py-3 bg-blue-600 rounded-xl text-white font-semibold disabled:opacity-40 active:bg-blue-700">
                      {saving ? t('item.saving') : t('common.save')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-1">
                  {[
                    [t('item.quantity'), `${item.quantity} ${['g','kg','ml','l','cl','dl'].includes(item.unit) ? 'stk' : (item.unit || 'stk')}`],
                    [t('item.location'), item.location_name || '—'],
                    [t('item.dateAdded'), item.date_added ? new Date(item.date_added).toLocaleDateString() : '—'],
                    item.expiry_date && [t('item.expiryDate'), new Date(item.expiry_date).toLocaleDateString()],
                    item.weight_per_unit && ['g','kg','ml','l','cl','dl'].includes(item.unit) && [t('item.size'), `${item.weight_per_unit}${item.unit} per stk`],
                    item.barcode && [t('item.barcode'), item.barcode],
                    item.opened && ['Status', t('item.openedStatus') + (item.opened_at ? ` ${new Date(item.opened_at).toLocaleDateString()}` : '')],
                    item.notes && [t('item.notes'), item.notes],
                  ].filter(Boolean).map(([k, v]) => (
                    <div key={k} className="flex justify-between py-2.5 border-b border-slate-700">
                      <span className="text-slate-400 text-sm">{k}</span>
                      <span className="text-white text-sm font-medium">{v}</span>
                    </div>
                  ))}
                </div>
              )}
              {!editing && (
                <button onClick={onDelete}
                  className="w-full py-3 mt-2 border border-red-700 rounded-xl text-red-400 active:bg-red-900/30">
                  {t('item.remove')}
                </button>
              )}
            </div>
          )}

          {/* IMAGES TAB */}
          {tab === 'images' && (
            <div className="space-y-4">
              {item.thumbnail && images.length === 0 && (
                <div>
                  <p className="text-xs text-slate-500 mb-2">{t('catalog.offThumbnail')}</p>
                  <img src={item.thumbnail} alt=""
                    className="w-full max-h-56 object-contain rounded-xl bg-slate-700" />
                </div>
              )}
              <ImageManager
                productId={item.product_id}
                images={images}
                onImagesChange={evt => {
                  if (evt.type === 'uploaded') setImages(prev => [...prev, evt.image]);
                  if (evt.type === 'delete') setImages(prev => prev.filter(i => i.id !== evt.id));
                }}
              />
            </div>
          )}

          {/* MOVE TAB */}
          {tab === 'move' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <p style={{ fontSize: 13, color: 'var(--fg-3)', fontFamily: 'DM Sans, sans-serif' }}>
                {t('item.moveTo')}.
              </p>

              {/* Antal */}
              <div>
                <label style={{ fontSize: 12, color: 'var(--fg-3)', fontFamily: 'DM Sans, sans-serif', display: 'block', marginBottom: 8 }}>{t('item.moveQty')}</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button type="button" onClick={() => setMoveQty(q => Math.max(1, q - 1))}
                    style={{ width: 48, height: 48, borderRadius: 12, border: 'none', background: 'var(--bg-elevated)', fontSize: 22, fontWeight: 700, cursor: 'pointer', color: 'var(--fg-1)', flexShrink: 0 }}>−</button>
                  <input type="number" min="1" max={item.quantity} value={moveQty}
                    onChange={e => setMoveQty(Math.min(item.quantity, Math.max(1, parseInt(e.target.value)||1)))}
                    style={{ flex: 1, textAlign: 'center', fontSize: 22, fontWeight: 700 }}
                    className="input-field" />
                  <button type="button" onClick={() => setMoveQty(q => Math.min(item.quantity, q + 1))}
                    style={{ width: 48, height: 48, borderRadius: 12, border: 'none', background: 'var(--bg-elevated)', fontSize: 22, fontWeight: 700, cursor: 'pointer', color: 'var(--fg-1)', flexShrink: 0 }}>+</button>
                </div>
                <p style={{ fontSize: 11, color: 'var(--fg-4)', textAlign: 'center', marginTop: 4, fontFamily: 'DM Sans, sans-serif' }}>{t('detail.inStockTotal', { count: item.quantity, unit: t('units.' + (['g','kg','ml','l','cl','dl'].includes(item.unit) ? 'stk' : (item.unit || 'stk')), item.unit || 'stk') })}</p>
              </div>

              {/* Destinations-valg: alle lister + enheder */}
              <div>
                <label style={{ fontSize: 12, color: 'var(--fg-3)', fontFamily: 'DM Sans, sans-serif', display: 'block', marginBottom: 8 }}>{t('item.moveTo')}</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {(() => {
                    // Find nuværende enhed
                    const currentUnitId = null; // Vis ALLE enheder — brugeren vælger selv destination
                    const ICONS = { freezer: '❄️', fridge: '🥛', pantry: '🥫' };

                    const unitButtons = storageUnits.map(unit => {
                      const lt = unit.list_type;
                      const locked = unit.position > 0 && !isPremium;
                      const isSelected = moveTarget === lt && moveTargetUnit === unit.id;
                      return (
                        <button key={unit.id} type="button"
                          onClick={() => {
                            if (locked) { setShowPaywall(true); return; }
                            setMoveTarget(lt);
                            setMoveTargetUnit(unit.id);
                            setMoveTargetLocation(null);
                          }}
                          style={{
                            padding: '12px 14px', borderRadius: 12,
                            border: isSelected ? '2px solid var(--brand)' : '1px solid var(--border-subtle)',
                            background: isSelected ? 'oklch(55% 0.19 152 / 12%)' : 'var(--bg-elevated)',
                            cursor: locked ? 'default' : 'pointer', textAlign: 'left',
                            opacity: locked ? 0.55 : 1, display: 'flex', alignItems: 'center', gap: 10,
                          }}>
                          <span style={{ fontSize: 20 }}>{locked ? '🔒' : (unit.icon || ICONS[lt])}</span>
                          <div style={{ flex: 1 }}>
                            <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--fg-1)', fontFamily: 'DM Sans, sans-serif' }}>{unit.name}</p>
                            <p style={{ fontSize: 11, color: 'var(--fg-3)', fontFamily: 'DM Sans, sans-serif' }}>{LIST_LABELS[lt]}</p>
                          </div>
                          {isSelected && <span style={{ color: 'var(--brand)', fontSize: 16 }}>✓</span>}
                        </button>
                      );
                    });

                    // Location picker for selected unit
                    const selectedUnitLocs = moveTargetUnit
                      ? allLocations.filter(l => l.storage_unit_id === moveTargetUnit)
                      : [];
                    const locPicker = selectedUnitLocs.length > 0 ? (
                      <div style={{ marginTop: 2, paddingLeft: 4 }}>
                        <p style={{ fontSize: 11, color: 'var(--fg-4)', fontFamily: 'DM Sans, sans-serif', marginBottom: 4 }}>{t('item.location')}</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {[{ id: null, name: t('item.noLocation') }, ...selectedUnitLocs].map(loc => {
                            const locSelected = moveTargetLocation === loc.id;
                            return (
                              <button key={loc.id ?? 'none'} type="button"
                                onClick={() => setMoveTargetLocation(loc.id)}
                                style={{
                                  padding: '8px 12px', borderRadius: 10, textAlign: 'left',
                                  border: locSelected ? '2px solid var(--brand)' : '1px solid var(--border-subtle)',
                                  background: locSelected ? 'oklch(55% 0.19 152 / 12%)' : 'var(--bg-card)',
                                  fontSize: 13, color: 'var(--fg-1)', fontFamily: 'DM Sans, sans-serif',
                                  cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                }}>
                                {loc.name}
                                {locSelected && <span style={{ color: 'var(--brand)', fontSize: 14 }}>✓</span>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ) : null;

                    return [...unitButtons, locPicker];
                  })()}
                </div>
              </div>

              <button type="button" onClick={moveItems} disabled={moving || moveTarget === listType}
                className="ft-btn-primary" style={{ marginTop: 4 }}>
                {(() => {
                  if (moving) return `⏳ ${t('item.moving')}`;
                  const countUnit = ['g','kg','ml','l','cl','dl'].includes(item.unit) ? 'stk' : (item.unit || 'stk');
                  const destUnit = storageUnits.find(u => u.id === moveTargetUnit);
                  const destLoc = moveTargetLocation ? allLocations.find(l => l.id === moveTargetLocation) : null;
                  const destLabel = destUnit
                    ? `${destUnit.icon || { freezer: '🧊', fridge: '🥛', pantry: '🥫' }[moveTarget]} ${destUnit.name}${destLoc ? ' / ' + destLoc.name : ''}`
                    : LIST_LABELS[moveTarget];
                  return `${t('item.move')} ${moveQty} ${countUnit} → ${destLabel}`;
                })()}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
